import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth, hasPerm } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { ArticlePicker } from "../../components/ArticlePicker";
import { Plus, ArrowRight, Repeat, Trash2 } from "lucide-react";
import { toast } from "sonner";

export function TransformationTab({ articles }) {
  const { user } = useAuth();
  const [regles, setRegles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [execOpen, setExecOpen] = useState(null);
  const [execNombre, setExecNombre] = useState("1");
  const [executing, setExecuting] = useState(false);

  const [form, setForm] = useState({ nom: "", article_source_id: "", quantite_deduite: "", article_cible_id: "", quantite_ajoutee: "" });
  const [saving, setSaving] = useState(false);

  const canView = hasPerm(user, "stock", "transformation_view");
  const canCreate = hasPerm(user, "stock", "transformation_create");
  const canExecute = hasPerm(user, "stock", "transformation_execute");

  const load = async () => {
    try {
      const { data } = await api.get("/stock-transformations");
      setRegles(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (canView) load(); }, [canView]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!canView) return null;

  const resetForm = () => setForm({ nom: "", article_source_id: "", quantite_deduite: "", article_cible_id: "", quantite_ajoutee: "" });

  const creerRegle = async () => {
    if (!form.nom.trim() || !form.article_source_id || !form.article_cible_id || !form.quantite_deduite || !form.quantite_ajoutee) {
      toast.error("Tous les champs sont obligatoires");
      return;
    }
    setSaving(true);
    try {
      await api.post("/stock-transformations", {
        nom: form.nom.trim(),
        article_source_id: form.article_source_id, quantite_deduite: Number(form.quantite_deduite),
        article_cible_id: form.article_cible_id, quantite_ajoutee: Number(form.quantite_ajoutee),
      });
      toast.success("Transformation créée");
      setCreateOpen(false);
      resetForm();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const supprimerRegle = async (regle) => {
    if (!window.confirm(`Supprimer la transformation « ${regle.nom} » ?`)) return;
    try {
      await api.delete(`/stock-transformations/${regle.id}`);
      toast.success("Transformation supprimée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const executerRegle = async () => {
    const nombre = Number(execNombre);
    if (!nombre || nombre <= 0) {
      toast.error("Indiquez un nombre de transformations valide");
      return;
    }
    setExecuting(true);
    try {
      const { data } = await api.post(`/stock-transformations/${execOpen.id}/executer`, { nombre });
      toast.success(`Transformation effectuée : ${data.deduit} déduit(s), ${data.ajoute} ajouté(s)`);
      setExecOpen(null);
      setExecNombre("1");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div data-testid="transformation-tab">
      {canCreate && (
        <Button onClick={() => setCreateOpen(true)} className="gap-2 mb-4" data-testid="transformation-create-button">
          <Plus size={15} /> Créer une transformation
        </Button>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Chargement...</p>
      ) : regles.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">Aucune règle de transformation créée.</p>
      ) : (
        <div className="space-y-3">
          {regles.map((r) => (
            <div key={r.id} className="rounded-xl border border-border bg-card p-4" data-testid={`transformation-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{r.nom}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1.5 flex-wrap mt-1">
                    <span>{r.quantite_deduite}x {r.article_source_nom}</span>
                    <ArrowRight size={13} className="shrink-0" />
                    <span>{r.quantite_ajoutee}x {r.article_cible_nom}</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Stock actuel de « {r.article_source_nom} » : {r.stock_source_actuel} —{" "}
                    <span className={r.transformations_possibles > 0 ? "text-primary font-medium" : "text-destructive"}>
                      vous pouvez transformer {r.transformations_possibles} fois
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {canExecute && (
                    <Button
                      size="sm" variant="outline" className="gap-1.5"
                      disabled={r.transformations_possibles <= 0}
                      onClick={() => { setExecOpen(r); setExecNombre("1"); }}
                      data-testid={`transformation-executer-${r.id}`}
                    >
                      <Repeat size={13} /> Transformer
                    </Button>
                  )}
                  {canCreate && (
                    <button
                      type="button" onClick={() => supprimerRegle(r)}
                      className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-destructive"
                      title="Supprimer" data-testid={`transformation-delete-${r.id}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={(v) => { setCreateOpen(v); if (!v) resetForm(); }}>
        <DialogContent data-testid="transformation-create-dialog">
          <DialogHeader><DialogTitle>Créer une transformation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Nom de la transformation</label>
              <Input
                value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })}
                placeholder='ex. "Hydrogel transparent lot de 20"' data-testid="transformation-nom"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Article à déduire</label>
              <ArticlePicker
                articles={articles} value={form.article_source_id}
                onChange={(id) => setForm({ ...form, article_source_id: id })}
                testId="transformation-article-source"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Quantité à déduire</label>
              <Input
                type="number" min="0" step="any" value={form.quantite_deduite}
                onChange={(e) => setForm({ ...form, quantite_deduite: e.target.value })}
                placeholder="ex. 1" data-testid="transformation-quantite-deduite"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Article à ajouter</label>
              <ArticlePicker
                articles={articles} value={form.article_cible_id}
                onChange={(id) => setForm({ ...form, article_cible_id: id })}
                testId="transformation-article-cible"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Quantité à ajouter</label>
              <Input
                type="number" min="0" step="any" value={form.quantite_ajoutee}
                onChange={(e) => setForm({ ...form, quantite_ajoutee: e.target.value })}
                placeholder="ex. 20" data-testid="transformation-quantite-ajoutee"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button onClick={creerRegle} disabled={saving} data-testid="transformation-save">
              {saving ? "Création..." : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!execOpen} onOpenChange={(v) => !v && setExecOpen(null)}>
        <DialogContent data-testid="transformation-exec-dialog">
          <DialogHeader><DialogTitle>Transformer : {execOpen?.nom}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Disponible : {execOpen?.transformations_possibles} transformation(s) possible(s)
            ({execOpen?.stock_source_actuel} × « {execOpen?.article_source_nom} »)
          </p>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Combien de fois transformer ?</label>
            <Input
              type="number" min="1" max={execOpen?.transformations_possibles} value={execNombre}
              onChange={(e) => setExecNombre(e.target.value)} data-testid="transformation-exec-nombre"
              autoFocus
            />
          </div>
          {execOpen && execNombre && Number(execNombre) > 0 && (
            <p className="text-sm">
              Résultat : déduit <span className="font-semibold">{execOpen.quantite_deduite * Number(execNombre)}</span> × « {execOpen.article_source_nom} »,
              ajoute <span className="font-semibold">{execOpen.quantite_ajoutee * Number(execNombre)}</span> × « {execOpen.article_cible_nom} »
            </p>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setExecOpen(null)}>Annuler</Button>
            <Button onClick={executerRegle} disabled={executing} data-testid="transformation-exec-confirm">
              {executing ? "En cours..." : "Transformer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
